import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState, AppThunk } from '..';
import {
  OrganizationDetails, PrivateOrganizationDetails, RegisteredPhysician,
  PhysicianToRegister,
} from '../../generated-api';
import LimberAdminApi from '../../api/LimberAdminApi';

export interface OrganizationsError {
}

export interface OrganizationsState {
  loading: boolean;
  error: OrganizationsError | null;
  organizations: Array<OrganizationDetails> | null;
  myOrganization: PrivateOrganizationDetails | null;
  registeredPhysician: RegisteredPhysician | null;
}

const initialState: OrganizationsState = {
  loading: false,
  error: null,
  organizations: null,
  myOrganization: null,
  registeredPhysician: null,
};

export const organizationsSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    startLoading: (state) => { state.loading = true; },
    loadingFailed: (state, action: PayloadAction<OrganizationsError>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearError: (state) => { state.error = null; },

    loadOrganizationsSucceeded: (
      state, action: PayloadAction<Array<OrganizationDetails>>,
    ) => {
      state.loading = false;
      state.error = null;
      state.organizations = action.payload;
    },
    loadMyOrganizationSucceeded: (
      state, action: PayloadAction<PrivateOrganizationDetails>,
    ) => {
      state.loading = false;
      state.error = null;
      state.myOrganization = action.payload;
    },

    startRegisteringPhysician: (state) => {
      state.loading = true;
      state.error = null;
      state.registeredPhysician = null;
    },
    registerPhysicianSucceeded: (
      state, action: PayloadAction<RegisteredPhysician>,
    ) => {
      state.loading = false;
      state.error = null;
      state.registeredPhysician = action.payload;
    },
  },
});

const {
  startLoading, loadingFailed,
  loadOrganizationsSucceeded, loadMyOrganizationSucceeded,
  startRegisteringPhysician, registerPhysicianSucceeded,
} = organizationsSlice.actions;
export const { clearError } = organizationsSlice.actions;

export const loadOrganizations = (
  getAccessToken: () => Promise<string>,
): AppThunk => async (dispatch) => {
  try {
    dispatch(startLoading());
    const accessToken = await getAccessToken();
    const api = new LimberAdminApi({ accessToken });
    const organizations = await api.organizationsGet();
    dispatch(loadOrganizationsSucceeded(organizations));
  } catch (error) {
    console.error(error);
    dispatch(loadingFailed({
    }));
  }
};

export const loadMyOrganization = (
  getAccessToken: () => Promise<string>,
): AppThunk => async (dispatch) => {
  try {
    dispatch(startLoading());
    const accessToken = await getAccessToken();
    const api = new LimberAdminApi({ accessToken });
    const myOrganization = await api.organizationsMineGet();
    dispatch(loadMyOrganizationSucceeded(myOrganization));
  } catch (error) {
    console.error(error);
    dispatch(loadingFailed({
    }));
  }
};

export const registerPhysician = (
  physicianToRegister: PhysicianToRegister,
  getAccessToken: () => Promise<string>,
): AppThunk => async (dispatch) => {
  try {
    dispatch(startRegisteringPhysician());
    const accessToken = await getAccessToken();
    const api = new LimberAdminApi({ accessToken });
    const registeredPhysician =
      await api.physicianAccountsRegisterPost({ physicianToRegister });
    dispatch(registerPhysicianSucceeded(registeredPhysician));
  } catch (error) {
    console.error(error);
    dispatch(loadingFailed({
    }));
  }
};

export const selectOrganizations = (state: RootState) => state.organizations;

export default organizationsSlice.reducer;
